import { format } from 'date-fns';
import { X, ExternalLink, File, GitCommit } from 'lucide-react';
import { AgentLog } from '../../types';
import { Modal } from '../Common';

interface LogDetailProps {
  log: AgentLog;
  onClose: () => void;
}

export function LogDetail({ log, onClose }: LogDetailProps) {
  const timestamp = format(new Date(log.timestamp), 'PPpp');

  return (
    <Modal isOpen={true} onClose={onClose} title="Log Details" size="lg">
      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              log.success
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {log.success ? 'Success' : 'Failed'}
          </span>
          <span className="text-sm text-gray-500">{timestamp}</span>
        </div>

        {/* Error message if failed */}
        {!log.success && log.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{log.error}</p>
          </div>
        )}

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Agent ID
            </label>
            <p className="mt-1 font-mono text-sm">{log.agentId}</p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Agent Type
            </label>
            <p className="mt-1 text-sm">{log.agentType || '-'}</p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Session ID
            </label>
            <p className="mt-1 font-mono text-sm truncate">{log.sessionId}</p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Operation
            </label>
            <p className="mt-1 text-sm capitalize">
              {log.operation.replace('_', ' ')}
            </p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Workspace
            </label>
            <p className="mt-1 text-sm">{log.workspace}</p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider">
              Duration
            </label>
            <p className="mt-1 text-sm">{log.duration}ms</p>
          </div>
        </div>

        {/* Path */}
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wider">
            Path
          </label>
          <p className="mt-1 font-mono text-sm bg-gray-50 p-2 rounded">
            {log.path}
          </p>
        </div>

        {/* Metadata */}
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">
              Metadata
            </label>
            <div className="bg-gray-50 p-3 rounded space-y-2">
              {log.metadata.fileSize && (
                <div className="flex items-center gap-2 text-sm">
                  <File className="w-4 h-4 text-gray-400" />
                  <span>File Size: {log.metadata.fileSize} bytes</span>
                </div>
              )}
              {log.metadata.commitSha && (
                <div className="flex items-center gap-2 text-sm">
                  <GitCommit className="w-4 h-4 text-gray-400" />
                  <span>
                    Commit: {log.metadata.commitSha.substring(0, 7)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
